/**
 * What the Copilot chat panel (src/components/CopilotPanel.jsx) remembers between messages and sends
 * with each one. Kept here, beside the chat route, so the route's tests send exactly what the panel
 * sends. Imported by the browser bundle: plain functions, no Node imports.
 *
 * The panel remembers the quote card it last showed (quote actions with no number default to it) and
 * the supplier RFQ it last showed (LP-397). While an RFQ is in front, every message carries
 * active_rfq_id, so a change with no quote number ("use 1.8 lb/sf") goes to the RFQ and never to the
 * quote (routes.js draftIntent / argsMatchIntent).
 * Which one is in front: an answer that shows an RFQ (with or without a quote card) puts the RFQ in
 * front; an answer that shows only a quote card puts the quote back in front (the RFQ is let go); an
 * answer with neither changes nothing. A request that fails (a network error, a proxy timeout, an error
 * answer) keeps the RFQ in front and brings none forward (afterFailure).
 */

export const NO_ACTIVE = Object.freeze({ quote: null, rfq: null });

const label = (doc) => `${doc?.number || ''} V${doc?.version || ''}`.trim();
const last = (drafts, kind) => [...drafts].reverse().find((d) => d && d.kind === kind && d.id);

/** The panel's memory after an answer: prev plus the answer's drafts. */
export function nextActive(prev, drafts) {
  const list = Array.isArray(drafts) ? drafts : [];
  const quote = last(list, 'quote');
  const rfq = last(list, 'supplier_rfq');
  if (!quote && !rfq) return prev || NO_ACTIVE;
  return {
    quote: quote ? { id: quote.id, label: label(quote.quote) } : (prev?.quote || null),
    rfq: rfq ? { id: rfq.id, label: label(rfq.rfq) } : null,
  };
}

/** The panel's request body for one message (POST /api/copilot/chat). */
export function chatBody(message, history, active) {
  return {
    message,
    history,
    ...(active?.quote ? { active_quote_id: active.quote.id } : {}),
    ...(active?.rfq ? { active_rfq_id: active.rfq.id } : {}),
  };
}

/** The line above the input: what a change with no number applies to, or null. */
export function appliesTo(active) {
  if (active?.rfq) return `Changes apply to ${active.rfq.label}`;
  if (active?.quote) return `Changes apply to ${active.quote.label}`;
  return null;
}

/** "clear" on that line: lets go of the RFQ (the quote comes back in front), else of the quote. */
export function clearActive(active) {
  return active?.rfq ? { ...active, rfq: null } : NO_ACTIVE;
}

// A message that asks for an RFQ (or Concria's price): loosely, as letting go of the quote is the safe side.
const ASKS_RFQ = /\b(?:rfqs?|rfps?|requests?\s+for\s+(?:a\s+)?(?:price|prices|pricing|quote|quotation)s?)\b|\bask\s+(?:concria|benjamin|mika)\b/i;

/**
 * The panel's memory after a request failed. The panel reads nothing: an RFQ in front stays in front.
 * With only a quote in front, the quote stays in front, unless the lost message asked for an RFQ: the
 * server may have made it (for this quote or another: "Make an RFQ for Q-2026-004" with Q-2026-002 in
 * front), so the quote is let go too, and the next change with no number is asked back. An RFQ the lost
 * answer made is never brought forward: it is in the quote's RFQ list.
 */
export function afterFailure(active, message = '') {
  if (active?.rfq) return active;
  if (ASKS_RFQ.test(String(message || ''))) return NO_ACTIVE;
  return active || NO_ACTIVE;
}

/**
 * What the panel does with a question asked outside it. A question is never dropped: it is sent,
 * placed in the panel input as a draft, or kept where it was asked.
 */
export function askAction(question, { loading, input } = {}) {
  const text = String(question ?? '').trim();
  if (!text) return 'keep';
  if (!loading) return 'send';
  const draft = String(input ?? '').trim();
  return !draft || draft === text ? 'place' : 'keep';
}
